import React, { useMemo } from "react";
import { uniqueNoteImages } from "../lib/notes";

/**
 * Compact flat-lay popover anchored to a tile corner (no fade animations).
 */
const FlatLayPopover = ({ scent, visible }) => {
  const items = useMemo(
    () => uniqueNoteImages(scent?.notes || []),
    [scent]
  );
  if (!visible || !scent) return null;
  return (
    <div
      data-testid={`flatlay-popover-${scent.symbol}`}
      className="absolute z-40 pointer-events-none -top-3 left-1/2 -translate-x-1/2 -translate-y-full"
    >
          <div
            className="w-64 md:w-72 border border-black brutal-shadow overflow-hidden"
            style={{ background: "#FFFFFF" }}
          >
            {/* Header */}
            <div className="flex items-start justify-between px-3 pt-3 pb-1">
              <div className="min-w-0">
                <div className="overline text-[8px] tracking-[0.22em] text-black/55 truncate">
                  Briology · {scent.category}
                </div>
                <div className="font-serif-display font-black text-base leading-tight truncate">
                  {scent.name}
                </div>
              </div>
              <div className="font-serif-display font-black text-lg leading-none text-black/80 shrink-0 pl-2">
                {scent.symbol}
              </div>
            </div>

            {/* Bestseller ribbon — chic, minimalist, gold-on-cream */}
            {scent.bestseller && (
              <div
                data-testid={`flatlay-bestseller-${scent.symbol}`}
                className="mx-3 mt-1 mb-1 flex items-center justify-center gap-1.5 py-1 border border-[#C99A3A]/70 bg-[#FBF3E1]"
                style={{ letterSpacing: "0.22em" }}
              >
                <span className="text-[#C99A3A] text-[9px] leading-none">★</span>
                <span className="overline text-[8px] text-[#8A6415]">
                  Bestseller
                </span>
                <span className="text-[#C99A3A] text-[9px] leading-none">★</span>
              </div>
            )}

            {/* Flat-lay grid */}
            <div className="px-3 pb-2">
              <Arrangement items={items} />
            </div>

            {/* Footer */}
            <div className="border-t border-black/10 px-3 py-1.5 bg-black/[0.02]">
              <div className="overline text-[7.5px] tracking-[0.18em] text-black/60 leading-snug line-clamp-2">
                {scent.notes && scent.notes.length > 0
                  ? scent.notes.join(" · ")
                  : "Single-note fragrance"}
              </div>
            </div>
          </div>

          {/* Tiny tail pointing down to the tile */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2.5 h-2.5 rotate-45 bg-white border-r border-b border-black" />
    </div>
  );
};

const Arrangement = ({ items }) => {
  if (!items.length) {
    return (
      <div className="py-3 text-center text-black/50 italic font-serif-display text-xs">
        A pure single note.
      </div>
    );
  }

  // Pick a tight grid: up to 4 across
  const cols = items.length <= 2 ? items.length : items.length <= 4 ? items.length : 4;

  return (
    <div
      className="grid gap-x-2 gap-y-2 justify-items-center"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }}
    >
      {items.map(({ slug, src, note }) => (
        <div
          key={slug}
          className="flex flex-col items-center justify-start"
          data-testid={`flatlay-item-${slug}`}
        >
          <div
            className="photo-paste photo-paste--tilt-none bg-white"
            style={{ padding: "4px 4px 8px" }}
          >
            <div
              className="overflow-hidden bg-white"
              style={{ width: "64px", height: "64px" }}
            >
              <img
                src={src}
                alt={note}
                draggable={false}
                className="select-none block"
                style={{ width: "64px", height: "64px", objectFit: "cover" }}
                loading="eager"
              />
            </div>
          </div>
          <div className="mt-1 w-16 overline text-[7px] tracking-[0.12em] text-black/55 text-center leading-[1.15] line-clamp-2 break-words">
            {note}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FlatLayPopover;
